/**
 * Accept Invitation Page
 *
 * Public page where invited users create their account.
 * Validates invitation token and collects user details.
 */

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  UserPlus,
  Loader2,
  AlertCircle,
  Clock,
  CheckCircle2,
  ArrowRight,
  Eye,
  EyeOff,
  User,
  Lock,
  Ban,
} from 'lucide-react';
import {
  usePublicInvitation,
  useAcceptInvitation,
} from '../../hooks/useInvitations';
import type { UserRole } from '@guild/shared';

// Role configuration with colors
const roleConfig: Partial<Record<UserRole, { label: string; bg: string; text: string; dot: string }>> = {
  super_admin: {
    label: 'Super Admin',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    dot: 'bg-amber-500',
  },
  admin: {
    label: 'Admin',
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    dot: 'bg-indigo-500',
  },
  manager: {
    label: 'Manager',
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    dot: 'bg-violet-500',
  },
  trainer: {
    label: 'Trainer',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    dot: 'bg-blue-500',
  },
  operator: {
    label: 'Operator',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500',
  },
};

// Fallback for any legacy roles not in roleConfig
const defaultRoleStyle = { label: 'Unknown', bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' };

export function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>();
  const { data: invitation, isLoading, error } = usePublicInvitation(token ?? null);

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const acceptMutation = useAcceptInvitation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Validation
    if (!fullName.trim()) {
      setFormError('Full name is required');
      return;
    }

    if (fullName.trim().length < 2) {
      setFormError('Name must be at least 2 characters');
      return;
    }

    if (!password) {
      setFormError('Password is required');
      return;
    }

    if (password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    try {
      await acceptMutation.mutateAsync({
        token: token!,
        data: {
          fullName: fullName.trim(),
          password,
        },
      });
      setIsSuccess(true);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create account');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 size={32} className="text-guild-green animate-spin" />
        <p className="mt-4 text-sm text-gray-500">Loading invitation...</p>
      </div>
    );
  }

  // Error states
  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';

    // Check for specific error types
    if (errorMessage.includes('expired')) {
      return <ExpiredState />;
    }
    if (errorMessage.includes('accepted')) {
      return <AlreadyAcceptedState />;
    }
    if (errorMessage.includes('revoked')) {
      return <RevokedState />;
    }
    // Generic not found / invalid
    return <InvalidState />;
  }

  if (!invitation) {
    return <InvalidState />;
  }

  // Success state
  if (isSuccess) {
    return <SuccessState email={invitation.email} />;
  }

  const role = roleConfig[invitation.role as UserRole] || defaultRoleStyle;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-guild-mint/50">
            <UserPlus size={24} className="text-guild-green" />
          </div>
        </div>
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          You've been invited!
        </h1>
        <p className="mt-2 text-sm text-gray-500 leading-relaxed">
          <span className="font-medium text-gray-700">{invitation.inviterName}</span> has invited you to join{' '}
          <span className="font-medium text-gray-700">{invitation.organizationName}</span>
        </p>
      </div>

      {/* Invitation Details */}
      <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">Your role:</span>
            <span
              className={`
                inline-flex items-center gap-1.5 rounded-full px-2.5 py-1
                text-xs font-semibold
                ${role.bg} ${role.text}
              `}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${role.dot}`} />
              {role.label}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Clock size={12} />
            <span>Expires {formatExpiry(invitation.expiresAt)}</span>
          </div>
        </div>
      </div>

      {/* Registration Form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        {formError && (
          <div className="flex items-center gap-2 rounded-xl bg-red-50 border border-red-100 px-4 py-3">
            <div className="h-2 w-2 rounded-full bg-red-500" />
            <p className="text-sm font-medium text-red-700">{formError}</p>
          </div>
        )}

        {/* Full Name */}
        <div>
          <label
            htmlFor="fullName"
            className="block text-sm font-semibold text-gray-700 mb-2"
          >
            Full Name <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <User
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter your full name"
              className="
                w-full rounded-xl border border-gray-200 bg-white
                pl-11 pr-4 py-3 text-sm
                placeholder:text-gray-400
                focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                disabled:bg-gray-50 disabled:text-gray-500
                transition-all duration-200
              "
              disabled={acceptMutation.isPending}
              autoFocus
            />
          </div>
        </div>

        {/* Password */}
        <div>
          <label
            htmlFor="password"
            className="block text-sm font-semibold text-gray-700 mb-2"
          >
            Password <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Lock
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password (min. 8 characters)"
              className="
                w-full rounded-xl border border-gray-200 bg-white
                pl-11 pr-12 py-3 text-sm
                placeholder:text-gray-400
                focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                disabled:bg-gray-50 disabled:text-gray-500
                transition-all duration-200
              "
              disabled={acceptMutation.isPending}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {/* Confirm Password */}
        <div>
          <label
            htmlFor="confirmPassword"
            className="block text-sm font-semibold text-gray-700 mb-2"
          >
            Confirm Password <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Lock
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              className="
                w-full rounded-xl border border-gray-200 bg-white
                pl-11 pr-12 py-3 text-sm
                placeholder:text-gray-400
                focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                disabled:bg-gray-50 disabled:text-gray-500
                transition-all duration-200
              "
              disabled={acceptMutation.isPending}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={acceptMutation.isPending}
          className="
            group w-full flex items-center justify-center gap-2
            rounded-xl bg-guild-green px-5 py-3.5
            text-sm font-semibold text-white
            shadow-lg shadow-guild-green/20
            hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
            disabled:opacity-50 disabled:cursor-not-allowed
            active:scale-[0.98]
            focus:outline-none focus:ring-2 focus:ring-guild-green focus:ring-offset-2
            transition-all duration-200
          "
        >
          {acceptMutation.isPending ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Creating account...
            </>
          ) : (
            <>
              Create Account
              <ArrowRight
                size={18}
                className="opacity-50 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200"
              />
            </>
          )}
        </button>
      </form>

      {/* Help text */}
      <div className="pt-4 border-t border-gray-100">
        <p className="text-xs text-gray-400 leading-relaxed">
          Already have an account?{' '}
          <Link to="/login" className="text-guild-green hover:underline font-medium">
            Sign in instead
          </Link>
        </p>
      </div>
    </div>
  );
}

// ============================================
// HELPER FUNCTIONS & SUB-COMPONENTS
// ============================================

function formatExpiry(date: Date): string {
  const d = new Date(date);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMs < 0) return 'soon';
  if (diffHours < 1) return 'in less than an hour';
  if (diffHours < 24) return `in ${diffHours} hours`;
  if (diffDays === 1) return 'in 1 day';
  return `in ${diffDays} days`;
}

function SuccessState({ email }: { email: string }) {
  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 mb-4">
          <CheckCircle2 size={32} className="text-emerald-500" />
        </div>
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          Account Created!
        </h1>
        <p className="mt-2 text-sm text-gray-500 leading-relaxed">
          Your account has been created successfully. You can now sign in with{' '}
          <span className="font-medium text-gray-700">{email}</span>
        </p>
      </div>

      <Link
        to="/login"
        className="
          group w-full flex items-center justify-center gap-2
          rounded-xl bg-guild-green px-5 py-3.5
          text-sm font-semibold text-white
          shadow-lg shadow-guild-green/20
          hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
          active:scale-[0.98]
          transition-all duration-200
        "
      >
        Go to Login
        <ArrowRight
          size={18}
          className="opacity-50 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200"
        />
      </Link>
    </div>
  );
}

function InvalidState() {
  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 mb-4">
          <AlertCircle size={32} className="text-gray-400" />
        </div>
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          Invalid Invitation
        </h1>
        <p className="mt-2 text-sm text-gray-500 leading-relaxed">
          This invitation link is invalid or no longer exists. Please contact your administrator for a new invitation.
        </p>
      </div>

      <Link
        to="/login"
        className="
          group w-full flex items-center justify-center gap-2
          rounded-xl border-2 border-gray-200 bg-white px-5 py-3.5
          text-sm font-semibold text-gray-700
          hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900
          active:scale-[0.98]
          transition-all duration-200
        "
      >
        Back to Login
      </Link>
    </div>
  );
}

function ExpiredState() {
  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 mb-4">
          <Clock size={32} className="text-amber-500" />
        </div>
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          Invitation Expired
        </h1>
        <p className="mt-2 text-sm text-gray-500 leading-relaxed">
          This invitation has expired. Please contact your administrator to request a new invitation.
        </p>
      </div>

      <Link
        to="/login"
        className="
          group w-full flex items-center justify-center gap-2
          rounded-xl border-2 border-gray-200 bg-white px-5 py-3.5
          text-sm font-semibold text-gray-700
          hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900
          active:scale-[0.98]
          transition-all duration-200
        "
      >
        Back to Login
      </Link>
    </div>
  );
}

function AlreadyAcceptedState() {
  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-50 mb-4">
          <CheckCircle2 size={32} className="text-blue-500" />
        </div>
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          Already Accepted
        </h1>
        <p className="mt-2 text-sm text-gray-500 leading-relaxed">
          This invitation has already been accepted. If you've created your account, you can sign in now.
        </p>
      </div>

      <Link
        to="/login"
        className="
          group w-full flex items-center justify-center gap-2
          rounded-xl bg-guild-green px-5 py-3.5
          text-sm font-semibold text-white
          shadow-lg shadow-guild-green/20
          hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
          active:scale-[0.98]
          transition-all duration-200
        "
      >
        Go to Login
        <ArrowRight
          size={18}
          className="opacity-50 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200"
        />
      </Link>
    </div>
  );
}

function RevokedState() {
  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50 mb-4">
          <Ban size={32} className="text-red-500" />
        </div>
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          Invitation Cancelled
        </h1>
        <p className="mt-2 text-sm text-gray-500 leading-relaxed">
          This invitation has been cancelled. Please contact your administrator if you believe this is an error.
        </p>
      </div>

      <Link
        to="/login"
        className="
          group w-full flex items-center justify-center gap-2
          rounded-xl border-2 border-gray-200 bg-white px-5 py-3.5
          text-sm font-semibold text-gray-700
          hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900
          active:scale-[0.98]
          transition-all duration-200
        "
      >
        Back to Login
      </Link>
    </div>
  );
}
